import { FC, useState, useMemo, useEffect } from 'react';
import { Icon, Select, SelectOptionType, Input, Button, TextArea } from '@aidkitorg/component-library';
import { useLocalizedStrings } from '../../../../Localization';
import { InfoDict } from '../../../../Questions/Props';
import { Message } from '../../../Support/types';

interface ReplyBoxProps {
  onSendMessage: (message: string, channel: 'email' | 'sms' | 'whatsapp', sendAt?: Date) => Promise<void>;
  applicantInfo?: InfoDict;
  messages: Message[];
}

export const ReplyBox: FC<ReplyBoxProps> = ({ onSendMessage, applicantInfo, messages }) => {
  const L = useLocalizedStrings();
  const [replyMessage, setReplyMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [whatsAppKey] = useState("whatsapp");
  const [whatsAppWindowClosed, setWhatsAppWindowClosed] = useState(false);
  const [canSendWhatsApp, setCanSendWhatsApp] = useState(false);
  const [isScheduleMode, setIsScheduleMode] = useState(false);
  const [scheduledDateTime, setScheduledDateTime] = useState<string>('');

  // Check if the WhatsApp 24 hour window is closed (matching Comms.tsx logic)
  // If the last inbound message from an applicant was more than 24 hours ago, the window is closed.
  useEffect(() => {
    if (!canSendWhatsApp) {
      setWhatsAppWindowClosed(false);
      return;
    }

    let latestInboundWhatsAppMessage = messages?.filter(msg => {
      return msg.kind === 'whatsapp' && (msg.needs_attention || msg.status);
    })?.sort((msg1, msg2) => {
      return (new Date(msg2.created_at)).getTime() - (new Date(msg1.created_at)).getTime()
    })?.[0];

    if (latestInboundWhatsAppMessage) {
      const latestDate = (new Date(latestInboundWhatsAppMessage.created_at)).getTime();
      const timeDifference = (new Date()).getTime() - latestDate;
      const twentyFourHours = 24 * 60 * 60 * 1000;
      if (timeDifference < twentyFourHours) {
        setWhatsAppWindowClosed(false);
      } else {
        setWhatsAppWindowClosed(true);
      }
    } else {
      setWhatsAppWindowClosed(false);
    }
  }, [messages, canSendWhatsApp]);

  // Check if WhatsApp can be sent (matching Comms.tsx logic)
  useEffect(() => {
    if (whatsAppKey && applicantInfo?.[whatsAppKey] === 'yes') {
      setCanSendWhatsApp(true);
      return;
    }

    const applicantSentWhatsApp = messages?.find(m => {
      return m.kind === 'whatsapp'
    });

    setCanSendWhatsApp(!!applicantSentWhatsApp);
  }, [messages, whatsAppKey, applicantInfo]);

  // Determine available message types based on applicant info (matching Comms.tsx logic)
  const availableChannels = useMemo(() => {
    const channels: ('email' | 'sms' | 'whatsapp')[] = [];

    // Check if email is available
    if (applicantInfo?.email) {
      channels.push('email');
    }

    // Check if phone/SMS is available
    if (applicantInfo?.phone_number || applicantInfo?.phone) {
      channels.push('sms');
    }

    // Check if WhatsApp is available (must have phone AND canSendWhatsApp)
    if ((applicantInfo?.phone_number || applicantInfo?.phone) && canSendWhatsApp) {
      channels.push('whatsapp');
    }

    return channels;
  }, [applicantInfo, canSendWhatsApp]);

  // Create select options for channels
  const channelOptions: SelectOptionType[] = useMemo(() => {
    return availableChannels.map(channel => ({
      label: channel === 'email'
        ? L.applicant.comms.send_email
        : channel === 'whatsapp'
          ? L.applicant.comms.send_whatsapp
          : L.applicant.comms.send_text,
      value: channel,
    }));
  }, [availableChannels, L.applicant.comms]);

  // Set initial channel (default to first available)
  const [selectedChannel, setSelectedChannel] = useState<SelectOptionType | undefined>(
    channelOptions.length > 0 ? channelOptions[0] : undefined
  );

  // Update selected channel when options change
  useMemo(() => {
    if (channelOptions.length > 0 && !selectedChannel) {
      setSelectedChannel(channelOptions[0]);
    }
  }, [channelOptions, selectedChannel]);

  // Determine if scheduling is allowed (not allowed for email)
  const canSchedule = selectedChannel?.value !== 'email';

  // Clear scheduled time and exit schedule mode when switching to email
  useEffect(() => {
    if (selectedChannel?.value === 'email') {
      setScheduledDateTime('');
      setIsScheduleMode(false);
    }
  }, [selectedChannel]);

  const handleSend = async () => {
    if (!replyMessage.trim() || !selectedChannel) return;

    setSending(true);
    try {
      const sendAt = scheduledDateTime ? new Date(scheduledDateTime) : undefined;
      await onSendMessage(replyMessage, selectedChannel.value as 'email' | 'sms' | 'whatsapp', sendAt);
      setReplyMessage('');
      setScheduledDateTime('');
      setIsScheduleMode(false);
    } finally {
      setSending(false);
    }
  };

  const handleScheduleClick = () => {
    setIsScheduleMode(!isScheduleMode);
    if (isScheduleMode) {
      // If disabling schedule mode, clear the scheduled date
      setScheduledDateTime('');
    }
  };

  return (
    <div className="bg-white border-t-2">
      {/* To: Channel Selector - Above everything */}
      {channelOptions.length > 0 && (
        <>
          <div className="p-3">
            <div className="flex items-center gap-2">
              <div className="w-40">
                <Select
                  label={L.applicant.comms.channel}
                  hideLabel={true}
                  description={L.applicant.comms.select_communication_channel}
                  value={selectedChannel}
                  onChange={setSelectedChannel}
                  options={channelOptions}
                  disabled={channelOptions.length === 1 || sending}
                />
              </div>
              <span className="text-sm font-medium text-gray-700 whitespace-nowrap">{L.applicant.comms.to.replace('$to', '').replace('To: ', 'To:')}</span>
            </div>
          </div>
          {/* Divider with margins */}
          <div className="px-3">
            <div className="border-b border-gray-200"></div>
          </div>
        </>
      )}

      <div className="p-3 space-y-3">
        <TextArea
          label={L.applicant.comms.reply_to_this_case}
          showLabel={true}
          value={replyMessage}
          onChange={(e) => setReplyMessage(e.target.value)}
          placeholder={L.applicant.comms.type_your_message_here}
          rows={3}
          disabled={sending}
          className="resize-none"
        />

        {/* Schedule DateTime Input - Only show when schedule mode is active */}
        {isScheduleMode && (
          <div className="w-full">
            <Input
              type="datetime-local"
              label={L.applicant.comms.schedule_send_time}
              value={scheduledDateTime}
              onChange={(e) => setScheduledDateTime(e.target.value)}
              disabled={sending}
            />
          </div>
        )}

        <div className="flex items-center justify-between">
          <div className="text-xs text-gray-500">
            {L.applicant.comms.messages_will_automatically_refresh}
          </div>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              onClick={handleScheduleClick}
              variant={isScheduleMode ? "primary" : "secondary"}
              size="md"
              icon={<Icon name="CalendarIcon" className="h-4 w-4" />}
              iconPlacement="left"
              isDisabled={sending || !canSchedule}
            >
              {isScheduleMode ? L.applicant.comms.cancel_schedule : L.applicant.comms.schedule}
            </Button>
            <Button
              type="button"
              onClick={handleSend}
              variant="primary"
              size="md"
              icon={<Icon name="PaperAirplaneIcon" className="h-4 w-4" />}
              iconPlacement="left"
              isDisabled={sending || !replyMessage.trim() || !selectedChannel}
              loading={sending}
            >
              {L.applicant.comms.send}
            </Button>
          </div>
        </div>
      </div>

      {/* WhatsApp window closed warning (matching Comms.tsx) */}
      {whatsAppWindowClosed && (
        <div className="p-3">
          <div className="rounded-md bg-yellow-50 p-4">
            <div className="flex">
              <div className="flex-shrink-0 mt-3">
                <Icon name="ExclamationTriangleIcon" className="h-7 w-7 text-yellow-400" />
              </div>
              <div className="ml-3">
                <div className="text-sm text-yellow-700 -mb-4">
                  <p>{L.applicant.comms.outside_whatsapp_window}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
